import { watch } from 'vue'

import { closeConnectorSurfaces } from '../service/openConnectorRegistry'

import { useWorkspace } from '@/features/account'
import { registerSessionReset } from '@/features/auth'

/**
 * Install module 12's scope edges once from the app composition root (ORP-20384).
 *
 * This closes SURFACES only. The DATA edges are already owned elsewhere and are deliberately not
 * duplicated here: `'integrations'` and `'integrations_connected'` are 07 bootstrap sections, so
 * `flows/bootstrapHydrators.ts` resets the store on both edges through the hydrator registry.
 * Everything below is the half nothing owned — an open dialog outlives a store reset.
 *
 * The workspace edge is a `watch` on 07's `workspaceId` (the `registerDataExportLifecycle` /
 * `registerAlertsLifecycle` idiom) rather than a hydrator reset, because a hydrator's `reset` also
 * fires on the ordinary cold-boot path, where closing nothing is correct and closing everything
 * would be an invisible no-op today but a trap the moment a connector opens during boot.
 */
export function registerIntegrationsLifecycle(): () => void {
  const { workspaceId } = useWorkspace()
  const stopWorkspace = watch(
    workspaceId,
    (next, previous) => {
      // Same-id re-emits happen on re-hydration; only a real switch closes the user's dialog.
      if (String(next ?? '') !== String(previous ?? '')) closeConnectorSurfaces()
    },
    { flush: 'sync' },
  )
  const unregisterSession = registerSessionReset(closeConnectorSurfaces)
  return () => {
    stopWorkspace()
    unregisterSession()
  }
}
